import React from 'react';
import { useIntl } from 'react-intl';
import { formatMessage } from '@99designs/frontend-components/i18n';
import FormattedReactMessage from '@99designs/frontend-components/i18n/FormattedReactMessage';
import { FAQAccordion, FAQAccordionProps } from '../../../../shared/components/Accordion';
import { ChameleonLink } from '../../../../shared/components/Link';
import {
    blogImageFileTypesPath,
    designerSearchPath,
    support,
} from '../../../../shared/lib/platformUrls';
import { pricingPath } from '../../../../shared/lib/platformUrls';

interface WinnersFAQsProps {
    categoryDeliverable: string;
}

interface BriefFAQsProps {
    categoryAndStartingPriceLabel: string;
}

const FAQs = ({ data }: FAQAccordionProps) => (
    <FAQAccordion
        data={data.map(({ id, title, child }) => ({
            id,
            title,
            child: <div className="paragraph paragraph--soft paragraph--no-margin">{child}</div>,
        }))}
    />
);

export const WinnersFAQs = ({ categoryDeliverable }: WinnersFAQsProps) => {
    const { locale } = useIntl();
    return (
        <FAQs
            data={[
                {
                    id: 'contest-end',
                    title: __('What happens at the end of a contest?'),
                    child: __(
                        'Choose a design you love and work closely with the winning designer to refine and finalize your design. After that they’ll supply the files needed for print or digital use. Full copyright is all yours.'
                    ),
                },
                {
                    id: 'files-received',
                    title: __('What files do I get?'),
                    child: (
                        <FormattedReactMessage
                            message={__(
                                'You’ll get all the <Link>image files</Link> you need to put your design anywhere. This includes the original, editable file, as well as a preview version you can open on virtually any device.'
                            )}
                            replacements={{
                                Link: <ChameleonLink href={blogImageFileTypesPath(locale)} />,
                            }}
                        />
                    ),
                },
                {
                    id: 'work-directly-with-designer',
                    title: formatMessage(
                        __(
                            'Can I work directly with a designer to create a {categoryDeliverable}?'
                        ),
                        {
                            categoryDeliverable: categoryDeliverable,
                        }
                    ),
                    child: (
                        <FormattedReactMessage
                            message={__(
                                'If you know exactly what you’re looking for, it makes sense to work with an individual freelance designer. In that case, you can search through our <Link>designers’ portfolios</Link> and find your perfect match.'
                            )}
                            replacements={{
                                Link: <ChameleonLink href={designerSearchPath()} />,
                            }}
                        />
                    ),
                },
            ]}
        />
    );
};

export const StatsSectionFAQs = () => {
    const { locale } = useIntl();

    return (
        <FAQs
            data={[
                {
                    id: 'designer-quality',
                    title: __('How do you screen for designer quality?'),
                    child: (
                        <FormattedReactMessage
                            message={__(
                                'We have a dedicated quality team that ranks designers from Entry, Mid and Top Level. Find out more about <Link>designer levels</Link>.'
                            )}
                            replacements={{
                                Link: <ChameleonLink href={support.designerLevelUrl(locale)} />,
                            }}
                        />
                    ),
                },
                {
                    id: 'money-back-guarantee',
                    title: __('What if I don’t like my design options?'),
                    child: (
                        <FormattedReactMessage
                            message={__(
                                'We offer a <MoneyBackLink>100% money-back guarantee</MoneyBackLink> within 60 days of payment on all design contests, except those with a <GuarenteedLink>guaranteed designer prize</GuarenteedLink> and those that have entered the Final Round.'
                            )}
                            replacements={{
                                MoneyBackLink: (
                                    <ChameleonLink href={support.moneyBackUrl(locale)} />
                                ),
                                GuarenteedLink: (
                                    <ChameleonLink href={support.guaranteedContestUrl(locale)} />
                                ),
                            }}
                        />
                    ),
                },
                {
                    id: 'contest-length',
                    title: __('How long does it take?'),
                    child: __(
                        'Most design contests take about a week, however there are options to expedite the process if necessary.'
                    ),
                },
            ]}
        />
    );
};

export const BriefFAQs = ({ categoryAndStartingPriceLabel }: BriefFAQsProps) => (
    <FAQs
        data={[
            {
                id: '99designs-details',
                title: __('What is 99designs?'),
                child: __(
                    '99designs is the global creative platform that makes it easy for designers and clients to work together to create designs they love.'
                ),
            },
            {
                id: 'design-contest-details',
                title: __('What is a design contest?'),
                child: __(
                    'A contest allows you to open your design brief to our global community of creative designers. Designers submit concepts based on your needs and you pick your favorite as the winner.'
                ),
            },
            {
                id: 'pricing-details',
                title: __('How much does it cost?'),
                child: (
                    <>
                        <FormattedReactMessage
                            message={__(
                                'Every design category has <Link>flexible pricing</Link> for all budgets.'
                            )}
                            replacements={{
                                Link: <ChameleonLink href={pricingPath()}></ChameleonLink>,
                            }}
                        />
                        {` ${categoryAndStartingPriceLabel}`}
                    </>
                ),
            },
        ]}
    />
);
